/**
 * Customer Login Action
 * Triggered by: arc:action:customerLogin
 */

export const runCustomerLogin = async (data = {}) => {
    try {
        const { 
            email, 
            password, 
            form,
            returnUrl = null,
            rememberMe = false,
            redirectOnSuccess = true 
        } = data;

        if (!email || !password) {
            throw new Error('Email and password are required');
        }

        // Prepare form data
        const formData = new FormData();
        formData.append('form_type', 'customer_login');
        formData.append('utf8', '✓');
        formData.append('customer[email]', email);
        formData.append('customer[password]', password);
        
        if (returnUrl) {
            formData.append('return_url', returnUrl);
        }

        // Add remember me if specified
        if (rememberMe) {
            formData.append('customer[remember_me]', '1');
        }

        // Dispatch login attempt event
        window.dispatchEvent(new CustomEvent('customer:loginAttempt', {
            detail: {
                email,
                rememberMe,
                returnUrl
            }
        }));

        // Submit login request
        const response = await fetch('/account/login', {
            method: 'POST',
            body: formData,
            headers: {
                'Accept': 'application/json, text/html',
            }
        });

        if (response.ok) {
            // Check if response is JSON (error) or HTML (success redirect)
            const contentType = response.headers.get('content-type');
            
            if (contentType && contentType.includes('application/json')) {
                // JSON response usually means error
                const errorData = await response.json();
                throw new Error(errorData.message || 'Login failed');
            } else {
                // HTML response usually means success and redirect
                // Dispatch success event
                window.dispatchEvent(new CustomEvent('customer:loginSuccess', {
                    detail: {
                        email,
                        redirectUrl: returnUrl || '/account',
                        timestamp: Date.now()
                    }
                }));

                if (redirectOnSuccess) {
                    // Handle redirect
                    const redirectUrl = returnUrl || '/account';
                    window.location.href = redirectUrl;
                }

                return {
                    success: true,
                    email,
                    redirectUrl: returnUrl || '/account',
                    message: 'Login successful'
                };
            }
        } else {
            // Handle HTTP errors
            let errorMessage = 'Login failed';
            
            try {
                const errorText = await response.text();
                // Try to extract error message from HTML response
                const parser = new DOMParser();
                const doc = parser.parseFromString(errorText, 'text/html');
                const errorElement = doc.querySelector('.errors, .error, [data-error]');
                if (errorElement) {
                    errorMessage = errorElement.textContent.trim();
                }
            } catch (e) {
                console.error('Error parsing response:', e);
            }

            throw new Error(errorMessage);
        }

    } catch (error) {
        console.error('Customer Login error:', error);
        
        window.dispatchEvent(new CustomEvent('customer:loginError', {
            detail: {
                error: error.message,
                email: data.email
            }
        }));

        return {
            success: false,
            error: error.message
        };
    }
};

// Initialize login form listeners
export const initializeCustomerLogin = (config = {}) => {
    const {
        formSelector = '[data-customer-login-form]',
        emailSelector = '[name="customer[email]"]',
        passwordSelector = '[name="customer[password]"]',
        submitSelector = '[type="submit"]',
        rememberMeSelector = '[name="customer[remember_me]"]',
        autoSetupForms = true
    } = config;

    if (autoSetupForms) {
        setupLoginForms(formSelector, emailSelector, passwordSelector, submitSelector, rememberMeSelector);
    }

    window.dispatchEvent(new CustomEvent('customerLogin:initialized', {
        detail: { config, timestamp: Date.now() }
    }));
};

// Setup login form event listeners
const setupLoginForms = (formSelector, emailSelector, passwordSelector, submitSelector, rememberMeSelector) => {
    const forms = document.querySelectorAll(formSelector);
    
    forms.forEach(form => {
        form.removeEventListener('submit', handleLoginSubmit);
        form.addEventListener('submit', handleLoginSubmit);
    });
};

// Handle login form submissions
const handleLoginSubmit = async (event) => {
    event.preventDefault();
    
    const form = event.target;
    const formData = new FormData(form);
    
    const email = formData.get('customer[email]');
    const password = formData.get('customer[password]');
    const rememberMe = formData.get('customer[remember_me]') === '1';
    const returnUrl = formData.get('return_url') || form.dataset.returnUrl;

    // Disable submit button to prevent double submission
    const submitBtn = form.querySelector('[type="submit"]');
    const originalText = submitBtn?.textContent;
    if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Signing in...';
    }

    try {
        await window.dispatchEvent(new CustomEvent('arc:action:customerLogin', {
            detail: {
                email,
                password,
                rememberMe,
                returnUrl,
                form,
                redirectOnSuccess: true
            }
        }));
    } finally {
        // Re-enable submit button
        if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.textContent = originalText;
        }
    }
};

export default runCustomerLogin;
